import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Voice } from '@/types/assistant'

// Query key factory
export const voiceKeys = {
  all: ['voices'] as const,
  lists: () => [...voiceKeys.all, 'list'] as const,
  list: (filters: VoiceFilters) => [...voiceKeys.lists(), filters] as const,
}

export interface VoiceFilters {
  language?: string
  age_group?: string
  gender?: string
  tone?: string
  pricing?: string
  languages?: string[]
  q?: string
  suggested?: boolean
}

/**
 * Hook to fetch available voices with optional filters
 */
export function useVoices(filters: VoiceFilters = {}, enabled = true) {
  return useQuery({
    queryKey: voiceKeys.list(filters),
    queryFn: async (): Promise<Voice[]> => {
      const params = new URLSearchParams()

      if (filters.language) params.set('language', filters.language)
      if (filters.age_group) params.set('age_group', filters.age_group)
      if (filters.gender) params.set('gender', filters.gender)
      if (filters.tone) params.set('tone', filters.tone)
      if (filters.pricing) params.set('pricing', filters.pricing)
      if (filters.languages?.length) {
        filters.languages.forEach((lang) => params.append('languages', lang))
      }
      if (filters.q) params.set('q', filters.q)
      if (filters.suggested !== undefined)
        params.set('suggested', String(filters.suggested))

      const url = `/assistants/ra/voices${params.toString() ? `?${params.toString()}` : ''}`
      const response = await api.get(url)
      return response.data
    },
    enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
}

/**
 * Hook to get a specific voice by ID
 */
export function useVoice(voiceId: number | undefined, voices: Voice[] = []) {
  // Find voice in the provided list (no separate API call needed)
  return voices.find((v) => v.id === voiceId)
}

export default useVoices
